import { useState } from 'react';
import { userAPI } from '../utils/api';

function errorMessage(error, fallback) {
  return Object.values(error.response?.data?.errors || {})[0]?.[0] || error.response?.data?.message || fallback;
}

export default function AssessmentPanel({ assessments }) {
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});
  const [attempt, setAttempt] = useState(null);
  const [attemptMeta, setAttemptMeta] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const openQuiz = async (id) => {
    setBusy(true);
    setError('');

    try {
      const [{ data: quiz }, { data: started }] = await Promise.all([userAPI.getQuiz(id), userAPI.startQuiz(id)]);
      setActive({ kind: 'quiz', ...quiz.data });
      setAttempt(started.data.id);
      setAttemptMeta(started.data);
      setAnswers({});
      setResult(null);
    } catch (e) {
      setError(errorMessage(e, 'Unable to start quiz.'));
    } finally {
      setBusy(false);
    }
  };

  const submitQuiz = async () => {
    setBusy(true);
    setError('');

    try {
      const response = await userAPI.submitQuiz(attempt, answers);
      const review = await userAPI.getQuizAttempt(attempt);
      setResult({ ...response.data.data, review: review.data.data });
    } catch (e) {
      setError(errorMessage(e, 'Unable to submit quiz.'));
    } finally {
      setBusy(false);
    }
  };

  const openAssignment = async (id) => {
    setBusy(true);
    setError('');

    try {
      const response = await userAPI.getAssignment(id);
      setActive({ kind: 'assignment', ...response.data.data });
      setResult(null);
    } catch (e) {
      setError(errorMessage(e, 'Unable to load assignment.'));
    } finally {
      setBusy(false);
    }
  };

  const submitAssignment = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');

    const form = event.currentTarget;
    const data = new FormData(form);

    try {
      const response = await userAPI.submitAssignment(active.id, data);
      setResult(response.data.data);
      form.reset();
    } catch (e) {
      setError(errorMessage(e, 'Unable to submit assignment.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-zinc-800 bg-zinc-900 p-6" aria-labelledby="assessments-heading">
      <h2 id="assessments-heading" className="mb-4 text-2xl font-bold text-white">Assessments</h2>
      {error && <p role="alert" className="mb-3 rounded bg-red-950 p-3 text-red-200">{error}</p>}

      <div className="flex flex-wrap gap-2">
        {assessments?.quizzes?.map((quiz) => (
          <button disabled={busy} key={quiz.id} onClick={() => openQuiz(quiz.id)} className="rounded bg-yellow-500 px-4 py-2 text-black">
            Quiz: {quiz.title}
          </button>
        ))}
        {assessments?.assignments?.map((assignment) => (
          <button disabled={busy} key={assignment.id} onClick={() => openAssignment(assignment.id)} className="rounded bg-blue-600 px-4 py-2 text-white">
            Assignment: {assignment.title}
          </button>
        ))}
      </div>

      {busy && <p className="mt-4 text-gray-300" role="status">Loading...</p>}

      {active?.kind === 'quiz' && !result && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitQuiz();
          }}
          className="mt-5 space-y-5"
        >
          <h3 className="text-xl text-white">{active.title}</h3>
          <p className="text-sm text-gray-400">
            Attempt {attemptMeta?.attempt_number} of {active.max_attempts}
            {attemptMeta?.expires_at ? ` - Submit by ${new Date(attemptMeta.expires_at).toLocaleString('en-IN')}` : ''}
          </p>
          {active.instructions && <p className="text-gray-300">{active.instructions}</p>}

          {active.questions.map((question) => (
            <fieldset key={question.position} className="rounded border border-zinc-700 p-4">
              <legend className="text-white">{question.prompt}</legend>
              {question.options.map((option) => (
                <label key={option.position} className="mt-2 block text-gray-300">
                  <input
                    type={question.type === 'single_choice' ? 'radio' : 'checkbox'}
                    name={`q-${question.position}`}
                    onChange={(event) => setAnswers((value) => ({
                      ...value,
                      [question.position]: question.type === 'single_choice'
                        ? [option.position]
                        : event.target.checked
                          ? [...new Set([...(value[question.position] || []), option.position])]
                          : (value[question.position] || []).filter((selected) => selected !== option.position),
                    }))}
                  /> {option.text}
                </label>
              ))}
            </fieldset>
          ))}

          <button disabled={busy} className="rounded bg-emerald-600 px-4 py-2 text-white">Submit quiz</button>
        </form>
      )}

      {active?.kind === 'assignment' && !result && (
        <form onSubmit={submitAssignment} className="mt-5 space-y-3">
          <h3 className="text-xl text-white">{active.title}</h3>
          <p className="text-sm text-gray-400">
            {active.submissions.length} of {active.max_attempts} attempts used
            {active.due_at ? ` - Due ${new Date(active.due_at).toLocaleString('en-IN')}` : ''}
          </p>
          <div className="prose prose-invert" dangerouslySetInnerHTML={{ __html: active.instructions_html }} />
          {['text', 'both'].includes(active.submission_type) && (
            <textarea name="text_content" required maxLength={100000} className="w-full rounded bg-zinc-800 p-3 text-white" aria-label="Assignment response" />
          )}
          {['file', 'both'].includes(active.submission_type) && (
            <label className="block text-sm text-gray-300">
              Upload up to 5 PDF, Word, text, JPEG, or PNG files (10 MB each)
              <input type="file" name="files[]" required multiple className="mt-2 block text-white" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png" />
            </label>
          )}
          <button disabled={busy || active.submissions.length >= active.max_attempts} className="rounded bg-emerald-600 px-4 py-2 text-white disabled:opacity-50">
            Submit assignment
          </button>
        </form>
      )}

      {result && (
        <div role="status" className="mt-5 rounded bg-emerald-950 p-4 text-emerald-200">
          {active.kind === 'quiz' ? (
            <>
              <p>Score: {result.percentage}% - {result.passed ? 'Passed' : 'Not passed'}</p>
              {result.review?.answers?.map((answer) => (
                <p key={answer.position} className="mt-2 text-sm">
                  Question {answer.position + 1}: {answer.correct ? 'Correct' : 'Incorrect'}
                  {answer.explanation ? ` - ${answer.explanation}` : ''}
                </p>
              ))}
            </>
          ) : 'Assignment submitted successfully.'}
        </div>
      )}
    </section>
  );
}
